import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronRight, Facebook, Instagram, Youtube, User, Users, Home, Search, Building2, MessageCircle, MoreHorizontal } from 'lucide-react';
import api from '@/utils/api';

const LOGO_URL = 'https://ganesahomestate.com/images/logo.png';

const WebsiteNav = ({ settings }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', label: 'Home' },
    { to: '/properties', label: 'Properties' },
    { to: '/about', label: 'About Us' },
    { to: '/projects', label: 'Projects' },
    { to: '/gallery', label: 'Gallery' },
    { to: '/blog', label: 'Blog' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0A0A0F] text-[#DAA520] text-xs py-2 px-4 border-b border-[#1A1A25]">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="hidden md:flex gap-6">
            <a href={`tel:${settings?.phone || ''}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Phone size={12} /> {settings?.phone || '+91 9876543210'}
            </a>
            <a href={`mailto:${settings?.email || ''}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Mail size={12} /> {settings?.email || 'info@ganesahomestate.com'}
            </a>
          </div>
          <div className="flex items-center gap-4 md:gap-4 w-full md:w-auto justify-between md:justify-end">
            <Link to="/customer-login" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <User size={12} /> Customer Login
            </Link>
            <Link to="/agent-login" className="flex items-center gap-1.5 hover:text-white transition-colors">
              <Users size={12} /> Agent Login
            </Link>
            <div className="hidden md:flex items-center gap-3">
              {settings?.social?.facebook && <a href={settings.social.facebook} target="_blank" rel="noopener noreferrer"><Facebook size={14} /></a>}
              {settings?.social?.instagram && <a href={settings.social.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={14} /></a>}
              {settings?.social?.youtube && <a href={settings.social.youtube} target="_blank" rel="noopener noreferrer"><Youtube size={14} /></a>}
            </div>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <nav className={`sticky top-0 z-50 transition-all duration-500 ${scrolled ? 'bg-white/95 backdrop-blur-xl shadow-lg' : 'bg-white/90 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14 md:h-20">
            <Link to="/" className="flex items-center gap-3 group" data-testid="website-logo">
              <img src={LOGO_URL} alt="Ganesa Homestate" className="h-10 md:h-14 w-auto object-contain brightness-110 drop-shadow-[0_2px_8px_rgba(201,168,76,0.4)] group-hover:brightness-125 transition-all" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {links.map((l) => (
                <Link key={l.to} to={l.to}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                    isActive(l.to) ? 'text-[#DAA520] bg-[#DAA520]/10' : 'text-slate-700 hover:text-[#0A0A0F] hover:bg-slate-100'
                  }`}>
                  {l.label}
                  {isActive(l.to) && <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#DAA520] rounded-full" />}
                </Link>
              ))}
              <Link to="/contact"
                className="ml-3 px-6 py-2.5 bg-gradient-to-r from-[#DAA520] to-[#B8860B] text-[#0A0A0F] font-bold text-sm rounded-lg hover:shadow-lg hover:shadow-[#DAA520]/30 transition-all duration-300 hover:scale-105 active:scale-95"
                data-testid="nav-enquire-btn">
                Enquire Now
              </Link>
              <a href={`https://wa.me/${(settings?.phone || '919876543210').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="ml-2 w-10 h-10 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#1fb855] hover:scale-110 transition-all shadow-md shadow-[#25D366]/30"
                data-testid="nav-whatsapp-btn">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
            </div>

            {/* Mobile - WhatsApp + Phone + Menu */}
            <div className="flex items-center gap-2 lg:hidden">
              <a href={`https://wa.me/${(settings?.phone || '919876543210').replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-[#25D366] text-white shadow-sm">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              </a>
              <a href={`tel:${settings?.phone || ''}`} className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DAA520]/15 text-[#DAA520]">
                <Phone size={16} />
              </a>
              <button onClick={() => setOpen(!open)} className="text-slate-700 p-2">
                {open ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {open && (
          <div className="lg:hidden bg-white/98 backdrop-blur-xl border-t border-slate-200 animate-slideDown">
            <div className="px-4 py-4 space-y-1">
              {links.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
                  className={`block px-4 py-3 rounded-lg text-sm font-medium transition-all ${isActive(l.to) ? 'text-[#DAA520] bg-[#DAA520]/10' : 'text-slate-700 hover:text-[#0A0A0F] hover:bg-slate-50'}`}>
                  {l.label}
                </Link>
              ))}
              <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-200">
                <Link to="/customer-login" onClick={() => setOpen(false)} className="px-4 py-2.5 bg-slate-100 text-slate-700 text-sm rounded-lg text-center font-medium hover:bg-[#DAA520]/10 hover:text-[#DAA520]">
                  Customer Login
                </Link>
                <Link to="/agent-login" onClick={() => setOpen(false)} className="px-4 py-2.5 bg-slate-100 text-slate-700 text-sm rounded-lg text-center font-medium hover:bg-[#DAA520]/10 hover:text-[#DAA520]">
                  Agent Login
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

/* Mobile Bottom Navigation - App Like Experience */
const MobileBottomNav = ({ settings }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path;
  const [moreOpen, setMoreOpen] = useState(false);

  const tabs = [
    { to: '/', icon: Home, label: 'Home' },
    { to: '/properties', icon: Search, label: 'Search' },
    { to: '/projects', icon: Building2, label: 'Projects' },
    { to: '/contact', icon: MessageCircle, label: 'Contact' },
  ];

  return (
    <>
      {/* More Menu Overlay */}
      {moreOpen && (
        <div className="fixed inset-0 z-[99] bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setMoreOpen(false)}>
          <div className="absolute bottom-[68px] left-4 right-4 bg-[#0A0A0F] rounded-2xl border border-[#1A1A25] shadow-2xl p-4 space-y-1" onClick={e => e.stopPropagation()}>
            {[
              { to: '/about', label: 'About Us' },
              { to: '/blog', label: 'Blog' },
              { to: '/customer-login', label: 'Customer Login' },
              { to: '/agent-login', label: 'Agent Login' },
            ].map(item => (
              <button key={item.to} onClick={() => { navigate(item.to); setMoreOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-[#DAA520] hover:bg-[#DAA520]/10 transition-all">
                {item.label}
              </button>
            ))}
            {settings?.phone && (
              <a href={`tel:${settings.phone}`} className="block px-4 py-3 rounded-xl text-sm font-medium text-[#DAA520] hover:bg-[#DAA520]/10 transition-all">
                Call: {settings.phone}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Bottom Nav Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-[98] lg:hidden" data-testid="mobile-bottom-nav">
        <div className="bg-[#0A0A0F]/95 backdrop-blur-xl border-t border-[#1A1A25] safe-area-bottom">
          <div className="flex items-center justify-around h-[62px] max-w-lg mx-auto px-2">
            {tabs.map((tab) => (
              <Link key={tab.to} to={tab.to}
                className={`flex flex-col items-center justify-center gap-0.5 w-16 py-1.5 rounded-xl transition-all duration-300 ${
                  isActive(tab.to) ? 'text-[#DAA520]' : 'text-slate-500 active:text-slate-300'
                }`}>
                <tab.icon size={20} strokeWidth={isActive(tab.to) ? 2.5 : 1.5} />
                <span className="text-[10px] font-medium">{tab.label}</span>
                {isActive(tab.to) && <span className="w-4 h-0.5 bg-[#DAA520] rounded-full mt-0.5" />}
              </Link>
            ))}
            <button onClick={() => setMoreOpen(!moreOpen)}
              className={`flex flex-col items-center justify-center gap-0.5 w-16 py-1.5 rounded-xl transition-all duration-300 ${
                moreOpen ? 'text-[#DAA520]' : 'text-slate-500 active:text-slate-300'
              }`}>
              <MoreHorizontal size={20} strokeWidth={moreOpen ? 2.5 : 1.5} />
              <span className="text-[10px] font-medium">More</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

const WebsiteFooter = ({ settings, seoPages }) => {
  const locations = [...new Set((seoPages || []).map(p => p.location).filter(Boolean))].slice(0, 10);
  const propertyTypes = [...new Set((seoPages || []).map(p => p.property_type).filter(Boolean))].slice(0, 6);

  return (
    <footer className="bg-[#060D18] text-slate-400 pb-20 lg:pb-0">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src={LOGO_URL} alt="Ganesa Homestate" className="h-16 w-auto object-contain brightness-110 drop-shadow-[0_2px_10px_rgba(201,168,76,0.3)]" />
            </div>
            <p className="text-sm leading-relaxed mb-5">{settings?.footer_text || settings?.description || 'Premium real estate solutions with complete transparency.'}</p>
            <div className="flex gap-3">
              {settings?.social?.facebook && <a href={settings.social.facebook} className="w-9 h-9 rounded-lg bg-[#1A1A25] flex items-center justify-center hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all"><Facebook size={16} /></a>}
              {settings?.social?.instagram && <a href={settings.social.instagram} className="w-9 h-9 rounded-lg bg-[#1A1A25] flex items-center justify-center hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all"><Instagram size={16} /></a>}
              {settings?.social?.youtube && <a href={settings.social.youtube} className="w-9 h-9 rounded-lg bg-[#1A1A25] flex items-center justify-center hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all"><Youtube size={16} /></a>}
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#DAA520] rounded-full" /> Quick Links
            </h3>
            <div className="space-y-2.5">
              {[{to:'/',l:'Home'},{to:'/about',l:'About Us'},{to:'/properties',l:'Properties'},{to:'/projects',l:'Projects'},{to:'/blog',l:'Blog'},{to:'/contact',l:'Contact Us'}].map(lnk => (
                <Link key={lnk.to} to={lnk.to} className="flex items-center gap-2 text-sm hover:text-[#DAA520] transition-colors group">
                  <ChevronRight size={14} className="text-[#DAA520] group-hover:translate-x-1 transition-transform" /> {lnk.l}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#DAA520] rounded-full" /> Property Types
            </h3>
            <div className="space-y-2.5">
              {propertyTypes.map(pt => (
                <Link key={pt} to={`/location/${pt.toLowerCase().replace(/ /g, '-')}-in-${(locations[0] || 'india').toLowerCase()}`} className="flex items-center gap-2 text-sm hover:text-[#DAA520] transition-colors group">
                  <ChevronRight size={14} className="text-[#DAA520] group-hover:translate-x-1 transition-transform" /> {pt}
                </Link>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold text-base mb-5 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#DAA520] rounded-full" /> Contact Info
            </h3>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#DAA520] mt-0.5 shrink-0" />
                <span className="text-sm">{settings?.address || 'Office Address'}</span>
              </div>
              <a href={`tel:${settings?.phone}`} className="flex items-center gap-3 hover:text-[#DAA520] transition-colors">
                <Phone size={18} className="text-[#DAA520] shrink-0" />
                <span className="text-sm">{settings?.phone}</span>
              </a>
              <a href={`mailto:${settings?.email}`} className="flex items-center gap-3 hover:text-[#DAA520] transition-colors">
                <Mail size={18} className="text-[#DAA520] shrink-0" />
                <span className="text-sm">{settings?.email}</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {seoPages && seoPages.length > 0 && (
        <div className="border-t border-[#1A1A25] py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h4 className="text-white text-sm font-semibold mb-4">Popular Searches</h4>
            <div className="flex flex-wrap gap-2">
              {seoPages.slice(0, 30).map(sp => (
                <Link key={sp.slug} to={`/location/${sp.slug}`} className="text-xs px-3 py-1.5 bg-[#1A1A25] rounded-full hover:bg-[#DAA520] hover:text-[#0A0A0F] transition-all">
                  {sp.title}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-[#1A1A25] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-xs">
          {settings?.footer_disclaimer && (
            <p className="text-center mb-3 text-slate-500 leading-relaxed">{settings.footer_disclaimer}</p>
          )}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
            <p>&copy; {new Date().getFullYear()} {settings?.company_name || 'Ganesa Homestate Developers'}. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="/privacy" className="hover:text-[#DAA520]">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-[#DAA520]">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

/* Popup Banner Component */
const PopupBanner = ({ settings }) => {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (settings?.popup_enabled && settings?.popup_image_url) {
      const dismissed = sessionStorage.getItem('popup_dismissed');
      if (!dismissed) {
        const timer = setTimeout(() => setShow(true), 1200);
        return () => clearTimeout(timer);
      }
    }
  }, [settings]);

  const handleClose = () => {
    setShow(false);
    sessionStorage.setItem('popup_dismissed', '1');
  };

  if (!show || !settings?.popup_image_url) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-8" data-testid="popup-banner-overlay" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" style={{ animation: 'fadeIn 0.3s ease' }} />
      <div className="relative max-w-md w-full max-h-[80vh]" onClick={e => e.stopPropagation()} style={{ animation: 'scaleIn 0.4s cubic-bezier(0.16,1,0.3,1)' }}>
        <button onClick={handleClose} data-testid="popup-close-btn"
          className="absolute -top-4 -right-4 z-10 w-10 h-10 rounded-full bg-white text-slate-700 flex items-center justify-center shadow-2xl hover:bg-red-50 hover:text-red-600 transition-colors border-2 border-slate-200"
          aria-label="Close popup">
          <X size={22} strokeWidth={2.5} />
        </button>
        <img src={settings.popup_image_url} alt="Ganesa Homestate" className="w-full max-h-[75vh] object-contain rounded-2xl shadow-2xl" data-testid="popup-banner-image" />
      </div>
    </div>
  );
};

export const WebsiteLayout = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [seoPages, setSeoPages] = useState([]);
  const [schemaData, setSchemaData] = useState(null);

  useEffect(() => {
    api.get('/public/website-settings').then(r => setSettings(r.data)).catch(() => {});
    api.get('/public/seo-pages').then(r => setSeoPages(r.data)).catch(() => {});
    api.get('/public/schema-data').then(r => setSchemaData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* JSON-LD Structured Data for SEO */}
      {schemaData?.organization && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData.organization) }}
        />
      )}
      <WebsiteNav settings={settings} />
      <main>{typeof children === 'function' ? children({ settings, seoPages }) : children}</main>
      <WebsiteFooter settings={settings} seoPages={seoPages} />
      <MobileBottomNav settings={settings} />
      <PopupBanner settings={settings} />
    </div>
  );
};
